import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import CreditNoteTableIcon from "../../../assets/CreditNote-TableIcon.svg";
import CreditNotesTableStyles from "./CreditNotesTableStyle";

interface CreditNote {
  customerName: string;
  issuedDate: string;
  creditNoteId: string;
  amountCredited: number;
  invoiceNos?: string[];
  orderIds?: string[];
}

interface Props {
  data: CreditNote[];
}

const CreditNotesTable: React.FC<Props> = ({ data }) => {
  const totalAmount = data.reduce((sum, row) => sum + row.amountCredited, 0);

  const columns = [
    { id: "customerName", label: "Customer Name", align: "left" },
    { id: "issuedDate", label: "Issued Date", align: "center" },
    { id: "invoiceId", label: "Invoice ID", align: "center" },
    { id: "creditNoteId", label: "Credit Note ID", align: "center" },
    { id: "amountCredited", label: "Amount Credited", align: "center" },
  ] as const;

  const HeaderCell = ({
    label,
    align,
  }: {
    label: string;
    align: "left" | "center";
  }) => (
    <TableCell align={align}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent={align === "center" ? "center" : "flex-start"}
        gap={0.5}
      >
        <Typography sx={CreditNotesTableStyles.headerText}>{label}</Typography>
        <Box
          component="img"
          src={CreditNoteTableIcon}
          alt="sort icon"
          sx={CreditNotesTableStyles.headerIcon}
        />
      </Box>
    </TableCell>
  );

  return (
    <TableContainer
      component={Paper}
      sx={CreditNotesTableStyles.tableContainer}
    >
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <HeaderCell key={col.id} label={col.label} align={col.align} />
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((row, index) => (
            <TableRow key={row.creditNoteId}>
              <TableCell
                align="left"
                sx={CreditNotesTableStyles.cellCustomerName}
              >
                {row.customerName}
              </TableCell>
              <TableCell align="center" sx={CreditNotesTableStyles.cellDefault}>
                {row.issuedDate}
              </TableCell>
              <TableCell align="center" sx={CreditNotesTableStyles.cellDefault}>
                {row.invoiceNos && row.invoiceNos.length > 0
                  ? row.invoiceNos.join(", ")
                  : row.orderIds && row.orderIds.length > 0
                    ? row.orderIds.join(", ")
                    : "-"}
              </TableCell>
              <TableCell align="center" sx={CreditNotesTableStyles.cellDefault}>
                {row.creditNoteId}
              </TableCell>
              <TableCell align="center" sx={CreditNotesTableStyles.cellDefault}>
                {row.amountCredited.toLocaleString()}
              </TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell align="left" colSpan={4}>
              <Typography sx={CreditNotesTableStyles.totalLabel}>
                Total Amount
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography sx={CreditNotesTableStyles.totalValue}>
                {totalAmount.toLocaleString()}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CreditNotesTable;
